import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { BaseRestService } from './base-rest.service';
import { Audit, IAuditList } from '../models/audit';
import { UserService } from './user.service';
import { catchError, map, retry, take, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { User } from '../models/user';
import { messageService } from './messages/message.module';
import { DateHelper } from 'app/main/content/misc/date.module';
import { EventService } from './event.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

export enum enumAuditType {
  Added,
  Deleted,
  Modified,
  Error,
  Info,
  Purged,
  Download,
  Login,
  Logout,
}

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class AuditService extends BaseRestService<Audit, IAuditList> implements OnDestroy{

  viewSubject: Subscription;
  downloadFile: Subscription;

  constructor(http: HttpClient, private userService: UserService, private eventService: EventService, private dateHelper: DateHelper, protected router: Router) {
    super(AuditService, http, 'api/audit', 'audit', router);

    this.viewSubject = this.eventService.onViewSubject.subscribe((subject) => { 
      if (subject && subject.uid){
        this.viewsubject(subject.uid).subscribe();
      }
    });

    this.downloadFile = this.eventService.onFileDownloaded.subscribe((filename: string) => {
      if (filename){
        this.downloadfile(filename).subscribe();
      }
    })
  }

  ngOnDestroy(): void {
    this.viewSubject?.unsubscribe();
    this.downloadFile?.unsubscribe();
  }

  hydrate(item: Audit): Observable<Audit> {
    const hydrate$ = [];
    if ((item.createdById) && ((!item.createdBy) || (item.createdBy.id !== item.createdById))) {
      hydrate$.push(this.userService.getById(item.createdById).pipe(map((user: User) => {
        item.createdBy = new User(user);
        return item;
      })));
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        return item;
      }));
    }
    else {
      return of(item);
    }
  }

  purge(fromDate: Date | string, toDate: Date | string) {
    fromDate = this.dateHelper.getDateAsString(fromDate, 'DD-MMM-YYYY');
    toDate = this.dateHelper.getDateAsString(toDate, 'DD-MMM-YYYY');

    const getUrl = `${this.resourceUrl}/purge/${fromDate}/${toDate}`;
    this.debug('Get, url:' + getUrl);
    this.http
      .get(getUrl).pipe(
        take(1),
        retry({count:3, delay: 3000}),
        tap((result) => {
          if (result.toString() !== '0') {
            this.log(`Audit trail purged`);
            messageService.success('Audit trail purged');
          }
          else {
            messageService.info('No records found for purge between the entered dates');
          }
          this.clearCache();
          this.getAll();
        }),
        catchError(this.handleError<Audit>(`Purge audit trail`, true)),
      ).subscribe();
  }

  getAllDataForSubject(subject: any): Observable<Audit[]> {
    return (subject && subject.uid) ? this.getAllData(undefined, `subjectid=${subject.uid}`).pipe(map((data: any) => {
      return data.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
    })) : of([]);
  }

  login() {
    this.http.post(`${this.resourceUrl}/login`, undefined).pipe(take(1), catchError(this.handleError<Audit>('Login', true))).subscribe();
  }

  logout() {
    this.logoutAsync().subscribe();
  }

  logoutAsync(): Observable<any> {
    // return this.http.post(`${this.resourceUrl}/logout`, undefined).pipe(take(1), catchError(this.handleError<Audit>('Logout', true)));
    return this.http.post(`${this.resourceUrl}/logout`, undefined).pipe(take(1));
  }

  downloadfile(filename: string): Observable<any> {
    return this.http.post(`${this.resourceUrl}/downloadfile/${filename}`, undefined).pipe(take(1), catchError(this.handleError<Audit>('Download file', true)));
  }

  viewsubject(subjectUid: string): Observable<any> {
    return this.http.post(`${this.resourceUrl}/viewsubject/${subjectUid}`, undefined).pipe(take(1), catchError(this.handleError<Audit>('View Subject', true)));
  }

  // try not to use as is flagged as low priority issue by Pen test...
  // addAudit(auditType: enumAuditType, summary: string, details: string){
  //   const audit = new Audit();
  //   audit.details = details;
  //   audit.summary = summary;
  //   audit.type = Object.keys(enumAuditType).find((k) => enumAuditType[k] === auditType);
  //   this.save(audit);
  //  }
}
